import {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import { useTranslation } from "react-i18next";
import SwitchSelector from "react-switch-selector";
import useBodyClass from "../../common/hooks/useBodyClassName";
import { AutoCampaignList } from "./AutoCampaignList";
import { ActiveMonitoringTabs } from "../monitoring/Monitoring.styled";
import { StyledAutoCampaign } from "./AutoCampaign.styled";
import {useAccessToken} from "../../common/hooks/useAccessToken";
import {hideLoader, showLoader} from "../../store/common/slice";
import {sortApartmentNumbers} from "../../common/shared/statelessMethods";
import {parseApartmentPhone, parseApartmentTenantName} from "../../store/common/tenantInfo";
import CommentsModal from "../comments/CommentsModal";
import {NotificationManager} from "react-notifications";

const tabs = {
    active: 'active',
    stopped: 'stopped',
}

export function AutoCampaign ({ match }) {
    const { buildingID } = match.params;

    const { t } = useTranslation();
    const client = useAccessToken();
    const dispatch = useDispatch();

    const currentBuildingData = useSelector(state => state.buildings.currentBuildingData);

    const [currentTab, setCurrentTab] = useState(tabs.active);
    const [aptsInCampaign, setAptsInCampaign] = useState([]);
    const [aptsOutOfCampaign, setAptsOutOfCampaign] = useState([]);
    const [commentsModalOpened, setCommentsModalOpened] = useState(false);
    const [commentsModalApt, setCommentsModalApt] = useState(0);

    const fullAddress = currentBuildingData?.address?.full_address;

    useBodyClass('report');

    const components = {
        [tabs.active]: {
            component: AutoCampaignList,
            props: {
                buildingID,
                records: aptsInCampaign,
                inMonitoring: true,
                onAptNotesOpen,
                removeFromCampaign: removeFromCampaign,
                addToCampaign: addToCampaign,
            }
        },
        [tabs.stopped]: {
            component: AutoCampaignList,
            props: {
                buildingID,
                records: aptsOutOfCampaign,
                inMonitoring: false,
                onAptNotesOpen,
                removeFromCampaign: removeFromCampaign,
                addToCampaign: addToCampaign
            }
        }
    };

    useEffect(() => {
        if (!client) return;
        (async function () {
            dispatch(showLoader());

            const {inMonitoring, stopped} = await client.managers.monitoring.getState(buildingID);
            const process = apts => apts.sort((a, b) => sortApartmentNumbers(a.ApartmentNum, b.ApartmentNum)).map(apt => {
                apt.tenantPhone = parseApartmentPhone(apt.tenantDetails);
                apt.tenantName = parseApartmentTenantName(apt.tenantDetails);
                return apt;
            });

            setAptsInCampaign(process(inMonitoring));
            setAptsOutOfCampaign(process(stopped));

            dispatch(hideLoader());
        })();
    }, [client]);

    const tabComponent = components[currentTab];
    function onAptNotesOpen(aptNumber) {
        setCommentsModalApt(aptNumber);
        setCommentsModalOpened(true);
    }

    const onAptNotesClose = () => setCommentsModalOpened(false);

    async function removeFromCampaign(apartmentNumbers) {
        dispatch(showLoader());
        await client.managers.monitoring.removeFromCampaign(buildingID, apartmentNumbers);
        dispatch(hideLoader());
        NotificationManager.success(t('monitoring.activeMonitoring.successfullyStopped'));
        setTimeout(() => window.location.reload(), 2000);
    }

    async function addToCampaign(apartments) {
        dispatch(showLoader());
        await client.managers.monitoring.addToCampaign(buildingID, apartments);
        dispatch(hideLoader());
        NotificationManager.success(t('monitoring.activeMonitoring.successfullyStarted'));
        setTimeout(() => window.location.reload(), 2000);
    }
    //todo - check if it should remain/changed or be removed completely
    //if (!isAdmin) return <div>{t('searchPayments.noAdminRights')}</div>;

    return (
        <StyledAutoCampaign className="bllink-text">
            <h1 className='page-title text-only'>
                {fullAddress && `${t('buildings.nav_bar_auto_campaign')} - ${fullAddress}`}
            </h1>
            <div className='tabs'>
                <ActiveMonitoringTabs className={currentTab === tabs.active ? 'active-selected' : ''}>
                    <SwitchSelector
                        initialSelectedIndex={2}
                        options={[
                            {
                                label: t('monitoring.activeMonitoring.tabs.inMonitoring'),
                                value: tabs.active,
                                selectedBackgroundColor: 'white',
                            },
                            {
                                label: t('monitoring.activeMonitoring.tabs.withoutMonitoring'),
                                value: tabs.stopped,
                                selectedBackgroundColor: 'white',
                            }
                        ]}
                        selectedFontColor='#6E74FF'
                        fontColor='#ffffff'
                        backgroundColor='linear-gradient(90deg, #6C90FF -0.23%, #8567FF 92.15%)'
                        onChange={e => setCurrentTab(e)}
                    />
                </ActiveMonitoringTabs>
            </div>
            <tabComponent.component
                {...tabComponent.props}
            />
            <CommentsModal
                show={commentsModalOpened}
                buildingId={buildingID}
                onModalHide={onAptNotesClose}
                apartmentNum={commentsModalApt}
                prefetchComments={false}
            />
        </StyledAutoCampaign>
    );
}
